import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, MapPin, GitBranch, XCircle, GraduationCap, Target, Users, Plus, Pencil, Trash2, Layers, CreditCard, CircleDot, Copy, ExternalLink, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const MASTER_TABS = [
  { value: 'courses', label: 'Courses', icon: BookOpen, endpoint: 'courses', hasCode: true },
  { value: 'lead-sources', label: 'Lead Sources', icon: MapPin, endpoint: 'lead-sources' },
  { value: 'lead-triggers', label: 'Triggers', icon: Target, endpoint: 'lead-triggers' },
  { value: 'lead-stages', label: 'Stages', icon: GitBranch, endpoint: 'lead-stages' },
  { value: 'lost-reasons', label: 'Lost Reasons', icon: XCircle, endpoint: 'lost-reasons' },
  { value: 'entrance-exam-types', label: 'Exam Types', icon: GraduationCap, endpoint: 'entrance-exam-types' },
  { value: 'payment-modes', label: 'Payment Modes', icon: CreditCard, endpoint: 'payment-modes' },
  { value: 'payment-statuses', label: 'Payment Statuses', icon: CircleDot, endpoint: 'payment-statuses' },
  { value: 'users', label: 'Users', icon: Users, endpoint: 'users', isUsers: true },
  { value: 'consultants', label: 'Consultants', icon: UserCheck, endpoint: 'consultants', isConsultants: true },
];

export default function MastersPage() {
  const [activeTab, setActiveTab] = useState('courses');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [editForm, setEditForm] = useState({ name: '', code: '', is_active: true, sort_order: 1, annual_fees: '' });
  const [userEditDialog, setUserEditDialog] = useState({ open: false, user: null });
  const [userEditForm, setUserEditForm] = useState({ name: '', email: '', role: '', is_active: true });
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: '', email: '', role: 'counsellor' });
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [subCourseDialog, setSubCourseDialog] = useState({ open: false, course: null });
  const [subCourses, setSubCourses] = useState([]);
  const [allSubCourses, setAllSubCourses] = useState([]);
  const [subCourseForm, setSubCourseForm] = useState({ name: '', code: '' });
  const [editingSubId, setEditingSubId] = useState(null);
  const [editSubForm, setEditSubForm] = useState({ name: '', code: '' });
  const [consultants, setConsultants] = useState([]);
  const [addConsultantDialog, setAddConsultantDialog] = useState(false);
  const [consultantForm, setConsultantForm] = useState({ name: '', email: '', phone: '' });
  const [editConsultantDialog, setEditConsultantDialog] = useState({ open: false, item: null });
  const [editConsultantForm, setEditConsultantForm] = useState({ name: '', email: '', phone: '', is_active: true });

  const currentTab = MASTER_TABS.find(t => t.value === activeTab);

  const fetchItems = useCallback(() => {
    setLoading(true);
    if (currentTab?.isUsers) {
      API.get('/users').then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
    } else if (currentTab?.isConsultants) {
      API.get('/consultants').then(r => { setConsultants(r.data); setLoading(false); }).catch(() => setLoading(false));
    } else {
      API.get(`/masters/${activeTab}`).then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [activeTab, currentTab]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    if (activeTab === 'courses') {
      API.get('/sub-courses').then(r => setAllSubCourses(r.data)).catch(() => {});
    }
  }, [activeTab, items]);

  const openCreate = () => {
    setEditForm({ name: '', code: '', is_active: true, sort_order: items.length + 1, annual_fees: '' });
    setEditDialog({ open: true, item: null });
  };

  const openEdit = (item) => {
    setEditForm({ name: item.name, code: item.code || '', is_active: item.is_active, sort_order: item.sort_order, annual_fees: item.annual_fees || '' });
    setEditDialog({ open: true, item });
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editDialog.item) {
        await API.put(`/masters/${activeTab}/${editDialog.item.id}`, editForm);
        toast.success('Updated');
      } else {
        await API.post(`/masters/${activeTab}`, editForm);
        toast.success('Created');
      }
      setEditDialog({ open: false, item: null });
      fetchItems();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await API.delete(`/masters/${activeTab}/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openUserEdit = (u) => {
    setUserEditForm({ name: u.name || '', email: u.email || '', role: u.role, is_active: u.is_active });
    setUserEditDialog({ open: true, user: u });
  };

  const handleUserSave = async () => {
    try {
      await API.put(`/users/${userEditDialog.user.user_id}`, userEditForm);
      toast.success('User updated');
      setUserEditDialog({ open: false, user: null });
      fetchItems();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await API.delete(`/users/${userId}`);
      toast.success('User deleted');
      setUserEditDialog({ open: false, user: null });
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAddConsultant = async () => {
    if (!consultantForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      await API.post('/consultants', consultantForm);
      toast.success('Consultant added');
      setAddConsultantDialog(false);
      setConsultantForm({ name: '', email: '', phone: '' });
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSaveConsultant = async () => {
    try {
      await API.put(`/consultants/${editConsultantDialog.item.id}`, editConsultantForm);
      toast.success('Consultant updated');
      setEditConsultantDialog({ open: false, item: null });
      fetchItems();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteConsultant = async (id, name) => {
    if (!window.confirm(`Delete consultant "${name}"?`)) return;
    try {
      await API.delete(`/consultants/${id}`);
      toast.success('Consultant deleted');
      setEditConsultantDialog({ open: false, item: null });
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyFormLink = (token) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/consultant-form/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Form link copied to clipboard');
  };

  const handleAddUser = async () => {
    if (!addUserForm.name.trim() || !addUserForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    try {
      await API.post('/users', addUserForm);
      toast.success('User created');
      setAddUserDialog(false);
      setAddUserForm({ name: '', email: '', role: 'counsellor' });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const openSubCourses = async (course) => {
    setSubCourseDialog({ open: true, course });
    setSubCourseForm({ name: '', code: '' });
    setEditingSubId(null);
    try {
      const r = await API.get(`/sub-courses?course_id=${course.id}`);
      setSubCourses(r.data);
    } catch { setSubCourses([]); }
  };

  const addSubCourse = async () => {
    if (!subCourseForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      await API.post('/sub-courses', { ...subCourseForm, course_id: subCourseDialog.course.id });
      toast.success('Sub-course added');
      setSubCourseForm({ name: '', code: '' });
      const r = await API.get(`/sub-courses?course_id=${subCourseDialog.course.id}`);
      setSubCourses(r.data);
      API.get('/sub-courses').then(r2 => setAllSubCourses(r2.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveSubCourse = async () => {
    try {
      await API.put(`/sub-courses/${editingSubId}`, editSubForm);
      toast.success('Updated');
      setEditingSubId(null);
      const r = await API.get(`/sub-courses?course_id=${subCourseDialog.course.id}`);
      setSubCourses(r.data);
      API.get('/sub-courses').then(r2 => setAllSubCourses(r2.data));
    } catch { toast.error('Failed'); }
  };

  const deleteSubCourse = async (id) => {
    if (!window.confirm('Delete this sub-course?')) return;
    try {
      await API.delete(`/sub-courses/${id}`);
      toast.success('Deleted');
      const r = await API.get(`/sub-courses?course_id=${subCourseDialog.course.id}`);
      setSubCourses(r.data);
      API.get('/sub-courses').then(r2 => setAllSubCourses(r2.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const getSubCount = (courseId) => allSubCourses.filter(s => s.course_id === courseId).length;

  return (
    <div className="p-6 space-y-4" data-testid="masters-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Masters & Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage system configuration and lookup tables</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto">
          {MASTER_TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs gap-1.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid={`master-tab-${t.value}`}>
              <t.icon size={13} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {MASTER_TABS.filter(t => !t.isUsers && !t.isConsultants).map(tab => (
          <TabsContent key={tab.value} value={tab.value} className="mt-4">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-700">{items.length} items</p>
                <Button size="sm" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="add-master-btn">
                  <Plus size={14} className="mr-1" /> Add {tab.label.replace(/s$/, '')}
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Name</TableHead>
                    {tab.hasCode && <TableHead className="text-xs font-semibold text-slate-500 uppercase">Code</TableHead>}
                    {tab.value === 'courses' && <TableHead className="text-xs font-semibold text-slate-500 uppercase">Annual Fees</TableHead>}
                    {tab.value === 'courses' && <TableHead className="text-xs font-semibold text-slate-500 uppercase">Sub-Courses</TableHead>}
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...items].sort((a, b) => a.name.localeCompare(b.name)).map(item => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm font-medium text-slate-900">{item.name}</TableCell>
                      {tab.hasCode && <TableCell className="text-xs font-mono text-slate-500">{item.code}</TableCell>}
                      {tab.value === 'courses' && (
                        <TableCell className="text-sm font-medium text-slate-700">{item.annual_fees ? `₹${Number(item.annual_fees).toLocaleString('en-IN')}` : '—'}</TableCell>
                      )}
                      {tab.value === 'courses' && (
                        <TableCell>
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openSubCourses(item)} data-testid={`sub-courses-${item.id}`}>
                            <Layers size={12} /> {getSubCount(item.id)} specializations
                          </Button>
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge className={`text-xs ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(item.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium text-slate-700" data-testid="users-count">
                  {userRoleFilter === 'all' ? items.length : items.filter(u => u.role === userRoleFilter).length} users
                </p>
                <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                  <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="user-role-filter">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="admission_head">Admission Head</SelectItem>
                    <SelectItem value="counsellor">Counsellor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={() => setAddUserDialog(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
                <Plus size={14} className="mr-1" /> Add User
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Role</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(userRoleFilter === 'all' ? items : items.filter(u => u.role === userRoleFilter)).map(u => (
                  <TableRow key={u.user_id} className="hover:bg-slate-50">
                    <TableCell className="text-sm font-medium text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs capitalize">{u.role?.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openUserEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteUser(u.user_id, u.name)} data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        {/* Consultants Tab */}
        <TabsContent value="consultants" className="mt-4">
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <p className="text-sm font-medium text-slate-700">{consultants.length} consultants</p>
              <Button size="sm" onClick={() => setAddConsultantDialog(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-consultant-btn">
                <Plus size={14} className="mr-1" /> Add Consultant
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Phone</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Leads</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Form Link</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consultants.map(c => (
                  <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`consultant-row-${c.id}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{c.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.phone}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{c.leads_count || 0}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-600" onClick={() => copyFormLink(c.form_token)} data-testid={`copy-link-${c.id}`}>
                          <Copy size={12} className="mr-1" /> Copy
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/consultant-form/${c.form_token}`, '_blank')}>
                          <ExternalLink size={12} />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${c.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditConsultantForm({ name: c.name, email: c.email, phone: c.phone, is_active: c.is_active }); setEditConsultantDialog({ open: true, item: c }); }} data-testid={`edit-consultant-${c.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteConsultant(c.id, c.name)} data-testid={`delete-consultant-${c.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Master Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={v => setEditDialog({ open: v, item: editDialog.item })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editDialog.item ? 'Edit' : 'Add'} {currentTab?.label.replace(/s$/, '')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} data-testid="master-name-input" />
            </div>
            {currentTab?.hasCode && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase">Code</Label>
                <Input value={editForm.code} onChange={e => setEditForm(p => ({...p, code: e.target.value}))} />
              </div>
            )}
            {activeTab === 'courses' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase">Annual Fees (₹)</Label>
                <Input type="number" value={editForm.annual_fees} onChange={e => setEditForm(p => ({...p, annual_fees: e.target.value ? parseFloat(e.target.value) : ''}))} placeholder="e.g. 150000" data-testid="course-annual-fees" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Sort Order</Label>
              <Input type="number" value={editForm.sort_order} onChange={e => setEditForm(p => ({...p, sort_order: parseInt(e.target.value) || 1}))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editForm.is_active} onCheckedChange={v => setEditForm(p => ({...p, is_active: v}))} />
              <Label className="text-sm">Active</Label>
            </div>
            <Button onClick={handleSave} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="save-master-btn">
              {editDialog.item ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Edit Dialog */}
      <Dialog open={userEditDialog.open} onOpenChange={v => setUserEditDialog({ open: v, user: userEditDialog.user })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User — {userEditDialog.user?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Full Name</Label>
              <Input value={userEditForm.name} onChange={e => setUserEditForm(p => ({...p, name: e.target.value}))} data-testid="edit-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Email</Label>
              <Input type="email" value={userEditForm.email} onChange={e => setUserEditForm(p => ({...p, email: e.target.value}))} data-testid="edit-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Role</Label>
              <Select value={userEditForm.role} onValueChange={v => setUserEditForm(p => ({...p, role: v}))}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="admission_head">Admission Head</SelectItem>
                  <SelectItem value="counsellor">Counsellor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={userEditForm.is_active} onCheckedChange={v => setUserEditForm(p => ({...p, is_active: v}))} />
              <Label className="text-sm">Active</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUserSave} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">Update User</Button>
              <Button variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleDeleteUser(userEditDialog.user?.user_id, userEditDialog.user?.name)} data-testid="delete-user-dialog-btn">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserDialog} onOpenChange={setAddUserDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Full Name *</Label>
              <Input value={addUserForm.name} onChange={e => setAddUserForm(p => ({...p, name: e.target.value}))} placeholder="e.g. John Doe" data-testid="new-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Email *</Label>
              <Input type="email" value={addUserForm.email} onChange={e => setAddUserForm(p => ({...p, email: e.target.value}))} placeholder="e.g. john@mbscollege.org" data-testid="new-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Role</Label>
              <Select value={addUserForm.role} onValueChange={v => setAddUserForm(p => ({...p, role: v}))}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="admission_head">Admission Head</SelectItem>
                  <SelectItem value="counsellor">Counsellor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400">The user will be able to log in via Google using this email address.</p>
            <Button onClick={handleAddUser} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="create-user-btn">Create User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sub-Courses Management Dialog */}
      <Dialog open={subCourseDialog.open} onOpenChange={v => setSubCourseDialog({ open: v, course: subCourseDialog.course })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Sub-Courses / Specializations — {subCourseDialog.course?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="Specialization name" value={subCourseForm.name} onChange={e => setSubCourseForm(f => ({...f, name: e.target.value}))} data-testid="new-sub-course-name" className="flex-1" />
              <Input placeholder="Code (opt)" value={subCourseForm.code} onChange={e => setSubCourseForm(f => ({...f, code: e.target.value}))} className="w-24" />
              <Button size="sm" onClick={addSubCourse} className="bg-slate-900 hover:bg-slate-800" data-testid="add-sub-course-btn"><Plus size={14} /></Button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {subCourses.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No specializations added yet.</p>}
              {subCourses.map(sc => (
                <div key={sc.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md border border-slate-100">
                  {editingSubId === sc.id ? (
                    <>
                      <Input value={editSubForm.name} onChange={e => setEditSubForm(f => ({...f, name: e.target.value}))} className="flex-1 h-8 text-sm" />
                      <Input value={editSubForm.code} onChange={e => setEditSubForm(f => ({...f, code: e.target.value}))} className="w-20 h-8 text-sm" placeholder="Code" />
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={saveSubCourse}><Pencil size={12} /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingSubId(null)}><XCircle size={12} /></Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-medium text-slate-800">{sc.name}</span>
                      {sc.code && <span className="text-xs font-mono text-slate-400">{sc.code}</span>}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingSubId(sc.id); setEditSubForm({ name: sc.name, code: sc.code || '' }); }} data-testid={`edit-sub-${sc.id}`}><Pencil size={12} /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteSubCourse(sc.id)} data-testid={`del-sub-${sc.id}`}><Trash2 size={12} /></Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Add Consultant Dialog */}
      <Dialog open={addConsultantDialog} onOpenChange={setAddConsultantDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Consultant</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Full Name *</Label>
              <Input value={consultantForm.name} onChange={e => setConsultantForm(p => ({...p, name: e.target.value}))} placeholder="Consultant name" data-testid="consultant-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Email</Label>
              <Input type="email" value={consultantForm.email} onChange={e => setConsultantForm(p => ({...p, email: e.target.value}))} placeholder="email@example.com" data-testid="consultant-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Phone Number</Label>
              <Input value={consultantForm.phone} onChange={e => setConsultantForm(p => ({...p, phone: e.target.value}))} placeholder="Phone number" data-testid="consultant-phone-input" />
            </div>
            <p className="text-xs text-slate-400">A unique form link will be generated for this consultant to submit leads.</p>
            <Button onClick={handleAddConsultant} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-consultant-btn">Add Consultant</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Edit Consultant Dialog */}
      <Dialog open={editConsultantDialog.open} onOpenChange={v => setEditConsultantDialog({ open: v, item: editConsultantDialog.item })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Consultant — {editConsultantDialog.item?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Full Name</Label>
              <Input value={editConsultantForm.name} onChange={e => setEditConsultantForm(p => ({...p, name: e.target.value}))} data-testid="edit-consultant-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Email</Label>
              <Input type="email" value={editConsultantForm.email} onChange={e => setEditConsultantForm(p => ({...p, email: e.target.value}))} data-testid="edit-consultant-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Phone</Label>
              <Input value={editConsultantForm.phone} onChange={e => setEditConsultantForm(p => ({...p, phone: e.target.value}))} data-testid="edit-consultant-phone" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editConsultantForm.is_active} onCheckedChange={v => setEditConsultantForm(p => ({...p, is_active: v}))} />
              <Label className="text-sm">Active</Label>
            </div>
            {editConsultantDialog.item?.form_token && (
              <div className="p-2 bg-slate-50 rounded-lg">
                <Label className="text-xs font-semibold text-slate-400 uppercase">Form Link</Label>
                <p className="text-xs text-blue-600 break-all mt-1">{process.env.REACT_APP_BACKEND_URL}/consultant-form/{editConsultantDialog.item.form_token}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSaveConsultant} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-consultant-btn">Update</Button>
              <Button variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleDeleteConsultant(editConsultantDialog.item?.id, editConsultantDialog.item?.name)} data-testid="delete-consultant-dialog-btn">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
